import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import './AddDestination.css';

const AddDestination = () => {
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    cityName: '',
    airportName: '',
    airportCode: '',
    country: 'Nigeria',
    status: 'active'
  });

  const [errors, setErrors] = useState({});

  // Country options
  const countries = [
    'Nigeria',
    'Ghana',
    'United Kingdom',
    'United States',
    'United Arab Emirates',
    'Turkey',
    'Germany',
    'Ethiopia',
    'Qatar',
    'South Africa',
    'Kenya',
    'Egypt',
    'Morocco',
    'France',
    'Spain',
    'Italy',
    'Netherlands',
    'Canada',
    'Australia',
    'Other'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Basic validation
    if (!formData.cityName.trim()) {
      newErrors.cityName = 'Destination name (city) is required';
    }
    
    if (!formData.airportName.trim()) {
      newErrors.airportName = 'Airport name is required';
    }
    
    if (!formData.airportCode.trim()) {
      newErrors.airportCode = 'Airport code is required';
    } else if (formData.airportCode.length < 3 || formData.airportCode.length > 4) {
      newErrors.airportCode = 'Airport code must be 3-4 characters';
    } else if (!/^[A-Z]{3,4}$/.test(formData.airportCode.toUpperCase())) {
      newErrors.airportCode = 'Airport code must contain only letters';
    }
    
    if (!formData.country) {
      newErrors.country = 'Country is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Placeholder for save destination
      console.log('Saving destination:', formData);
      // Navigate back to destinations list
      navigate('/admin/destinations');
    }
  };

  const handleCancel = () => {
    navigate('/admin/destinations');
  };

  return (
    <AdminLayout pageTitle="Add Destination">
      <div className="admin-add-destination-page">
        {/* Page Title */}
        <div className="admin-add-destination-header">
          <h1 className="admin-add-destination-title">Add Destination</h1>
          <nav className="admin-breadcrumb">
            <button 
              className="admin-breadcrumb-link"
              onClick={() => navigate('/admin/destinations')}
            >
              Destinations
            </button>
            <span className="admin-breadcrumb-separator">→</span>
            <span className="admin-breadcrumb-current">Add Destination</span>
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="admin-destination-form">
          {/* Destination Form Card */}
          <div className="admin-form-section">
            <div className="admin-form-section-header">
              <h2 className="admin-form-section-title">Destination Information</h2>
            </div>
            <div className="admin-form-section-content">
              <div className="admin-form-grid">
                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Destination Name (City) <span className="admin-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="cityName"
                    value={formData.cityName}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.cityName ? 'error' : ''}`}
                    placeholder="e.g., Lagos"
                  />
                  {errors.cityName && (
                    <span className="admin-form-error">{errors.cityName}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Airport Name <span className="admin-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="airportName"
                    value={formData.airportName}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.airportName ? 'error' : ''}`}
                    placeholder="e.g., Murtala Muhammed International Airport"
                  />
                  {errors.airportName && (
                    <span className="admin-form-error">{errors.airportName}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Airport Code <span className="admin-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="airportCode"
                    value={formData.airportCode}
                    onChange={(e) => {
                      const upperValue = e.target.value.toUpperCase();
                      setFormData(prev => ({
                        ...prev,
                        airportCode: upperValue
                      }));
                      if (errors.airportCode) {
                        setErrors(prev => ({
                          ...prev,
                          airportCode: ''
                        }));
                      }
                    }}
                    className={`admin-form-input ${errors.airportCode ? 'error' : ''}`}
                    placeholder="e.g., LOS"
                    maxLength="4"
                  />
                  {errors.airportCode && (
                    <span className="admin-form-error">{errors.airportCode}</span>
                  )}
                  <span className="admin-form-hint">3-4 letter IATA code</span>
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Country <span className="admin-required">*</span>
                  </label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    className={`admin-form-select ${errors.country ? 'error' : ''}`}
                  >
                    {countries.map(country => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                  </select>
                  {errors.country && (
                    <span className="admin-form-error">{errors.country}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Status <span className="admin-required">*</span>
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="admin-form-select"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="admin-form-actions">
            <button
              type="button"
              className="admin-form-button admin-form-button-secondary"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="admin-form-button admin-form-button-primary"
            >
              Save Destination
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AddDestination;

